import { addDays, endOfISOWeek, formatISO, startOfISOWeek } from "date-fns";
import { z } from "zod";
import { type TaskFilter, taskFilter } from "@/shared/task-filter";

/** The fields a task list filters, groups and orders on; the full row carries more. */
export interface ViewedTask {
  readonly id: string;
  readonly status: "todo" | "in_progress" | "done";
  readonly priority: number;
  readonly orderKey: string;
  readonly parentId: string | null;
  readonly assigneeId: string | null;
  readonly delegateId: string | null;
  readonly dueOn: string | null;
  readonly dueTime: string | null;
}

/**
 * The four the filter menu offers; any other key an answer carries is dropped so each has a chip.
 * Foreday is listed among assignees, so `delegated__is` rides in the Assignee condition.
 */
export const FILTER_FIELDS = [
  "status__in",
  "assignee_id__in",
  "due_on__range",
  "priority__in",
] as const;

export type FilterField = (typeof FILTER_FIELDS)[number];

export const menuFilter = taskFilter.pick({
  status__in: true,
  assignee_id__in: true,
  due_on__range: true,
  priority__in: true,
  delegated__is: true,
});

export type MenuFilter = Pick<TaskFilter, FilterField | "delegated__is">;

const GROUPINGS = ["status", "assignee", "priority", "none"] as const;
const ORDERINGS = ["manual", "due", "priority"] as const;

export type Grouping = (typeof GROUPINGS)[number];
export type Ordering = (typeof ORDERINGS)[number];

/** Anything unreadable falls back to the default rather than refusing the page. */
export const viewSearch = z.object({
  filter: menuFilter.catch({}).default({}),
  group: z.enum(GROUPINGS).catch("status").default("status"),
  order: z.enum(ORDERINGS).catch("manual").default("manual"),
  /** The Done section's open state, closed by default. */
  done: z.boolean().catch(false).default(false),
});

export type ViewSearch = z.infer<typeof viewSearch>;

export const VIEW_DEFAULTS = viewSearch.parse({});

const iso = (day: Date) => formatISO(day, { representation: "date" });

/** The due-date choices the menu offers, as the range the filter stores. A week runs Monday to Sunday. */
export function dueRange(
  choice: "overdue" | "today" | "week",
  today: string,
): { from: string | null; to: string | null } {
  const day = new Date(`${today}T00:00:00`);
  if (choice === "overdue") return { from: null, to: iso(addDays(day, -1)) };
  if (choice === "today") return { from: today, to: today };
  return { from: iso(startOfISOWeek(day)), to: iso(endOfISOWeek(day)) };
}

function within(
  day: string | null,
  range: { from: string | null; to: string | null },
): boolean {
  if (day === null) return false;
  return (
    (range.from === null || day >= range.from) &&
    (range.to === null || day <= range.to)
  );
}

/** People and Foreday picked together are one condition: any of them holds it. */
function assigned(task: ViewedTask, filter: MenuFilter): boolean {
  const people = filter.assignee_id__in;
  const foreday = filter.delegated__is;
  if (!people && foreday === undefined) return true;
  return (
    (people?.includes(task.assigneeId) ?? false) ||
    (foreday !== undefined && foreday === (task.delegateId !== null))
  );
}

function matches(task: ViewedTask, filter: MenuFilter): boolean {
  return (
    (!filter.status__in || filter.status__in.includes(task.status)) &&
    assigned(task, filter) &&
    (!filter.priority__in ||
      filter.priority__in.some((level) => level === task.priority)) &&
    (!filter.due_on__range || within(task.dueOn, filter.due_on__range))
  );
}

// Plain `<`, not `localeCompare`: `order_key` is `COLLATE "C"`, so the database orders by bytes.
const byKey = (a: ViewedTask, b: ViewedTask) =>
  a.orderKey < b.orderKey ? -1 : a.orderKey > b.orderKey ? 1 : 0;

const ORDER: Record<Ordering, (a: ViewedTask, b: ViewedTask) => number> = {
  manual: byKey,
  due: (a, b) => {
    const when = (task: ViewedTask) =>
      task.dueOn === null ? "~" : `${task.dueOn} ${task.dueTime ?? "99"}`;
    const first = when(a);
    const second = when(b);
    return first < second ? -1 : first > second ? 1 : byKey(a, b);
  },
  priority: (a, b) => b.priority - a.priority || byKey(a, b),
};

const STATUS_ORDER = ["in_progress", "todo"] as const;

function keyOf(task: ViewedTask, group: Grouping): string {
  if (group === "status") return task.status;
  if (group === "assignee") return task.assigneeId ?? "";
  if (group === "priority") return String(task.priority);
  return "all";
}

export interface ViewGroup<T> {
  /** A status, a person's id ("" for nobody), a priority level, or "all". */
  readonly key: string;
  readonly tasks: T[];
}

/**
 * Filters, groups and orders the working set in memory: a new filter or sort is never a request.
 * Done tasks leave their groups for the Done section, whatever the grouping. `hidden` counts every
 * top-level task the filter leaves out, done ones included.
 */
export function arrange<T extends ViewedTask>(
  rows: readonly T[],
  view: ViewSearch,
): { groups: ViewGroup<T>[]; done: T[]; hidden: number } {
  const listed = rows.filter((task) => task.parentId === null);
  const kept = listed
    .filter((task) => matches(task, view.filter))
    .sort(ORDER[view.order]);
  const shown = kept.filter((task) => task.status !== "done");

  const groups = new Map<string, T[]>();
  if (view.group === "status") {
    for (const status of STATUS_ORDER) groups.set(status, []);
  }
  if (view.group === "priority") {
    for (const level of ["3", "2", "1", "0"]) groups.set(level, []);
  }
  for (const task of shown) {
    const key = keyOf(task, view.group);
    const group = groups.get(key);
    if (group) group.push(task);
    else groups.set(key, [task]);
  }

  return {
    groups: [...groups]
      .filter(([, tasks]) => tasks.length > 0)
      .map(([key, tasks]) => ({ key, tasks })),
    done: kept.filter((task) => task.status === "done"),
    hidden: listed.length - kept.length,
  };
}
